import { ClassesIllustration, OverviewIllustration, SubjectIllustration } from '@bp/ui-components';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { BaseOutlet } from '../../../../../components/BaseOutlet/BaseOutlet';
import { CurriculumBaseStats } from '../../../../../components/Curricula/components/CurriculumBaseStats';
import { CurriculumGradeStats } from '../../../../../components/Curricula/components/CurriculumGradeStats';
import { CurriculumSubjectStats } from '../../../../../components/Curricula/components/CurriculumSubjectStats';
import { IconMenu } from '../../../../../components/IconMenu/IconMenu';
import { IconMenuItem } from '../../../../../components/IconMenuItem/IconMenuItem';
import { Page } from '../../../../../components/Page/Page';
import { useAuthClaims } from '../../../../../hooks/useAuthClaims';
import { useMemorizedCacheTag } from '../../../../../hooks/useMemorizedCacheTag';
import { usePermissionChecker } from '../../../../../hooks/usePermissionChecker';
import { use_SubjectsQuery, useCurriculumVersionsListQuery } from '../../../../../types/planung-graphql-client-defs';

type CurriculumPageProps = {
  baseSlug: string;
};

export const CurriculumPage = ({ baseSlug }: CurriculumPageProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const permissions = usePermissionChecker();
  const navigate = useNavigate();
  const location = useLocation();
  const { curriculumUuid, grade, subjectUuid } = useParams();
  const curriculumVersionsContext = useMemorizedCacheTag('CURRICULUM_VERSIONS');
  const subjectsContext = useMemorizedCacheTag('SUBJECTS');

  const isBase = curriculumUuid ? location.pathname.endsWith(curriculumUuid) : false;
  const isGradeGroup = location.pathname.endsWith(`${t('routes.deputate.setup.grades.slug')}/${grade}`);
  const isSubject = location.pathname.endsWith(`${t('routes.deputate.setup.subjects.slug')}/${subjectUuid}`);

  const [{ data: curriculumVersionsData }] = useCurriculumVersionsListQuery({
    variables: {
      where: {
        organization: { uuid: pimAuthClaims.getOrganizationUuid() },
      },
    },
    context: curriculumVersionsContext,
    pause: !curriculumUuid,
  });

  const [{ data: subjectsData }] = use_SubjectsQuery({
    context: subjectsContext,
    pause: !curriculumUuid || !isSubject,
  });

  const curriculumVersionName = curriculumVersionsData?.curriculumVersions.find(
    (cv) => cv.uuid === curriculumUuid,
  )?.name;

  const subjectName = subjectsData?.subjects.find((s) => s.uuid === subjectUuid)?.name;

  return (
    <Page
      isForbidden={!permissions?.canReadDeputate()}
      title={curriculumVersionName}
      subheadline={
        isGradeGroup
          ? `${t('grades.title', { count: 1 })}: ${grade}`
          : isSubject
            ? `${t('subject.title', { count: 1 })}: ${subjectName}`
            : undefined
      }
    >
      <IconMenu hasMarginBottom noWrap>
        <IconMenuItem
          label={t('deputate.menuItems.overview')}
          icon={<OverviewIllustration />}
          bubbleType={5}
          onClick={() => navigate(`/${baseSlug}/${t('routes.curriculum.slug')}/${curriculumUuid}`)}
          active={isBase}
        />
        <IconMenuItem
          label={t('deputate.menuItems.grades')}
          icon={<ClassesIllustration />}
          bubbleType={6}
          onClick={() => navigate(t('routes.deputate.setup.grades.slug'))}
          active={location.pathname.includes(t('routes.deputate.setup.grades.slug'))}
        />
        <IconMenuItem
          label={t('deputate.menuItems.subject')}
          icon={<SubjectIllustration />}
          bubbleType={1}
          onClick={() => navigate(t('routes.deputate.setup.subjects.slug'))}
          active={location.pathname.includes(t('routes.deputate.setup.subjects.slug'))}
        />

        {isGradeGroup ? (
          <CurriculumGradeStats curriculumUuid={curriculumUuid} grade={grade} />
        ) : isSubject ? (
          <CurriculumSubjectStats curriculumUuid={curriculumUuid} subjectUuid={subjectUuid} />
        ) : (
          <CurriculumBaseStats curriculumUuid={curriculumUuid} />
        )}
      </IconMenu>
      <BaseOutlet />
    </Page>
  );
};
